/* eslint-disable import/prefer-default-export */
import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';
import { checkIfShouldSkipCurrentDay } from './checkIfShouldSkipCurrentDay';
import { getNumberOfNonCustomSessions } from './getNumberOfNonCustomSessions';
import calculateCurrentTimeWithOffset from './calculateCurrentTimeWithOffset';

export const getNonCustomSession = ({
  isRecurring,
  firstSession,
  recurringOption,
  numberOfSessions,
  endDate = null,
  endOption = 'after-sessions',
  startTime,
  endTime,
  exceptions,
  timezone = 'Europe/London',
  blockBooking,
}) => {
  if (!isRecurring || isRecurring === 'no' || !firstSession) return [];
  const sessions = [];
  const current = new Date(firstSession);
  let count = 0;

  const { number, unit } = blockBooking?.ticketSaleClose || {};

  const end =
    endOption === 'on-specific-day' && endDate
      ? moment(new Date(endDate)).tz('Europe/London').endOf('day')
      : null;
  const now = calculateCurrentTimeWithOffset({ number, unit }).toDate();

  // `numberOfSessions` is deliberately ignored unless the provider chose to end
  // "after N sessions" — a listing that ends on a specific date keeps whatever
  // value the other radio option left behind, and capping by it truncated 14 live
  // listings (CS-75: Brede Breakfast Club showed 10 of its 37 sessions, all past,
  // so the page offered the waitlist while Sharetribe had 27 of 30 seats free).
  const maxSessions =
    endOption === 'after-sessions' && numberOfSessions
      ? parseInt(numberOfSessions, 10)
      : getNumberOfNonCustomSessions({
          isRecurring,
          firstSession,
          recurringOption,
          endDate,
          endOption,
          timezone,
        });

  // Every read of `current` below interprets it in the listing's timezone, so the
  // steps have to move it in that timezone too. `Date.setDate`/`Date.getDay` work
  // in the runtime's zone, and under SSR (UTC) a BST midnight anchor still sits on
  // the previous day there — which stepped over every Monday of an every-weekday
  // term, and dropped the last session of an every-week one once the UK and the
  // runtime's clocks changed on different dates.
  const advanceDays = (days) => {
    current.setTime(moment(current).tz(timezone).startOf('day').add(days, 'days').valueOf());
  };

  let displayedSessionCount = 0;
  let displayedRemovedSessionCount = 0;
  let lastTimeHasRemovedSession = false;

  while (count < maxSessions) {
    if (end && current > end) break;
    const needToCheckCurrentDayOfWeek = recurringOption === 'every-week' || 'every-weekday';

    const shouldSkipAddingCurrentDay = needToCheckCurrentDayOfWeek
      ? checkIfShouldSkipCurrentDay({
          current,
          recurringOption,
          timezone,
          firstSession,
        })
      : false;

    if (!shouldSkipAddingCurrentDay) {
      const formattedDate = moment(current).tz(timezone).format('Do MMM, YYYY');
      const bookingStartTime = moment(current)
        .tz(timezone)
        .set({
          hour: startTime.split(':')[0],
          minute: startTime.split(':')[1],
        })
        .toDate()
        .getTime();
      const bookingEndTime = moment(current)
        .tz(timezone)
        .set({
          hour: endTime.split(':')[0],
          minute: endTime.split(':')[1],
        })
        .toDate()
        .getTime();

      const hasException = !!exceptions?.find((exception) => {
        const { start: exceptionStart, end: exceptionEnd } = exception;
        return bookingStartTime === exceptionStart && bookingEndTime === exceptionEnd;
      });

      if (hasException) {
        lastTimeHasRemovedSession = true;
      }

      sessions.push({
        label: !hasException
          ? `Session ${displayedSessionCount + 1}`
          : `Session ${displayedRemovedSessionCount + 1}`,
        date: formattedDate,
        rawDate: moment(cloneDeep(current)),
        startTime,
        endTime,
        past: bookingStartTime < now.getTime(),
      });

      count++;

      if (hasException && lastTimeHasRemovedSession) {
        displayedRemovedSessionCount++;
      }

      if (!hasException) {
        displayedSessionCount++;
        displayedRemovedSessionCount = displayedSessionCount;
        lastTimeHasRemovedSession = false;
      }
    }

    // Increment date AFTER adding the session to avoid skipping the first session
    if (count < maxSessions) {
      // Only increment if we need more sessions
      if (recurringOption === 'every-day') {
        advanceDays(1);
      } else if (recurringOption === 'every-week') {
        advanceDays(shouldSkipAddingCurrentDay ? 1 : 7);
      } else if (recurringOption === 'every-weekday') {
        do {
          advanceDays(1);
        } while ([0, 6].includes(moment(current).tz(timezone).day()));
      } else {
        break;
      }
    }
  }

  return sessions;
};
